<script>
  import { onMount } from "svelte";
  import { apiRequest } from "../utils/api";

  export let isAdmin = false;

  let users = [];
  let loading = false;
  let listError = null;

  let form = { email: "", password: "", role: "manager" };
  let creating = false;
  let createError = null;
  let createMessage = null;

  let actionError = null;

  onMount(() => {
    if (isAdmin) {
      loadUsers();
    }
  });

  async function loadUsers() {
    loading = true;
    listError = null;
    try {
      users = await apiRequest("/api/users");
    } catch (error) {
      listError = error?.message || "Could not load users.";
      users = [];
    } finally {
      loading = false;
    }
  }

  async function createUser(event) {
    event.preventDefault();
    if (creating) {
      return;
    }
    creating = true;
    createError = null;
    createMessage = null;
    try {
      const created = await apiRequest("/api/users", { method: "POST", body: { user: form } });
      createMessage = `Created ${created.email}.`;
      form = { email: "", password: "", role: "manager" };
      await loadUsers();
    } catch (error) {
      createError = error?.message || "Could not create user.";
    } finally {
      creating = false;
    }
  }

  async function changeRole(user, role) {
    actionError = null;
    try {
      await apiRequest(`/api/users/${user.id}`, { method: "PATCH", body: { user: { role } } });
      await loadUsers();
    } catch (error) {
      actionError = error?.message || "Could not update role.";
      await loadUsers();
    }
  }

  async function toggleBlock(user) {
    actionError = null;
    const action = user.blocked ? "unblock" : "block";
    try {
      await apiRequest(`/api/users/${user.id}/${action}`, { method: "POST" });
      await loadUsers();
    } catch (error) {
      actionError = error?.message || `Could not ${action} user.`;
    }
  }
</script>

{#if !isAdmin}
  <p class="text-neutral-500 text-xs">Admins only.</p>
{:else}
  <div class="space-y-4">
    <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-4" on:submit={createUser}>
      <h3 class="text-xs font-bold text-neutral-800 uppercase tracking-wider mb-3">Create User</h3>
      
      {#if createError}
        <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs mb-3" role="alert">{createError}</p>
      {/if}
      {#if createMessage}
        <p class="bg-success-bg text-success-text border border-success-border/20 rounded px-3 py-1.5 text-xs mb-3" role="status">{createMessage}</p>
      {/if}
      
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div class="form-group">
          <label class="form-label" for="user-email">Email Address</label>
          <input id="user-email" class="form-input" type="email" placeholder="name@example.com" bind:value={form.email} required autocomplete="off" />
        </div>
        
        <div class="form-group">
          <label class="form-label" for="user-password">Password</label>
          <input id="user-password" class="form-input" type="password" placeholder="Min 6 characters" bind:value={form.password} required autocomplete="new-password" />
        </div>
        
        <div class="grid grid-cols-2 gap-3 items-end">
          <div class="form-group">
            <label class="form-label" for="user-role">Role</label>
            <select id="user-role" class="form-input" bind:value={form.role}>
              <option value="manager">manager</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <button type="submit" class="btn-primary w-full" disabled={creating}>
            {creating ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
      
      <p class="text-[0.72rem] text-neutral-500 mt-2">No invitation email is sent — share the password directly.</p>
    </form>

    {#if actionError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert" data-testid="user-action-error">{actionError}</p>
    {/if}
    {#if listError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert">{listError}</p>
    {/if}

    {#if loading}
      <div class="border border-neutral-200 rounded-lg overflow-hidden animate-pulse">
        <div class="bg-neutral-50 h-8 border-b border-neutral-200"></div>
        <div class="divide-y divide-neutral-100">
          {#each Array(3) as _}
            <div class="flex items-center p-3.5 space-x-4">
              <div class="h-4 bg-neutral-200 rounded w-1/3"></div>
              <div class="h-4 bg-neutral-200 rounded w-1/4"></div>
              <div class="h-4 bg-neutral-200 rounded w-1/6"></div>
              <div class="h-6 bg-neutral-200 rounded w-16 ml-auto"></div>
            </div>
          {/each}
        </div>
      </div>
    {:else if users.length === 0}
      <div class="bg-white border border-neutral-200 rounded-xl p-8 text-center space-y-2 shadow-2xs">
        <p class="text-neutral-500 text-xs font-semibold">No users registered.</p>
        <p class="text-[0.7rem] text-neutral-400">Use the form above to add a new admin or manager.</p>
      </div>
    {:else}
      <div class="overflow-x-auto border border-neutral-200 rounded-lg">
        <table class="w-full text-left border-collapse text-xs" data-testid="user-table">
          <thead>
            <tr class="bg-neutral-50 border-b border-neutral-200">
              <th class="p-2.5 font-bold text-neutral-700">Email</th>
              <th class="p-2.5 font-bold text-neutral-700">Role</th>
              <th class="p-2.5 font-bold text-neutral-700">Status</th>
              <th class="p-2.5"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-100">
            {#each users as user (user.id)}
              <tr class="hover:bg-neutral-50/50 transition-colors {user.blocked ? 'user-blocked opacity-65 bg-neutral-50/50' : ''}">
                <td class="p-2.5 font-medium text-neutral-900">{user.email}</td>
                <td class="p-2.5">
                  <select class="form-input max-w-[110px] py-1 px-2 text-xs" value={user.role} on:change={(e) => changeRole(user, e.target.value)}>
                    <option value="manager">manager</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td class="p-2.5">
                  {#if user.blocked}
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase tracking-wider bg-danger-bg text-danger-text border border-danger-border/20">blocked</span>
                  {:else}
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold uppercase tracking-wider bg-success-bg text-success-text border border-success-border/20">active</span>
                  {/if}
                </td>
                <td class="p-2.5 text-right">
                  <button type="button" class="btn-secondary py-1 px-2.5 text-[0.78rem]" data-testid="toggle-block" on:click={() => toggleBlock(user)}>
                    {user.blocked ? "Unblock" : "Block"}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
{/if}
